import React, { useState, useEffect } from 'react';
import { superadminApi } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Bell, Play, Loader2, RefreshCw, Sun, CalendarDays, CalendarRange, Cake, Activity } from 'lucide-react';
import { format } from 'date-fns';

const fmt = (iso) => { if (!iso) return '—'; try { return format(new Date(iso), 'd MMM yyyy, HH:mm'); } catch { return iso; } };

const StatusPill = ({ status }) => {
  const map = { sent: 'text-emerald-600 bg-emerald-500/10', noop: 'text-muted-foreground bg-secondary', error: 'text-red-600 bg-red-500/10' };
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${map[status] || 'bg-secondary'}`}>{status}</span>;
};

const LastCell = ({ last }) => {
  if (!last) return <span className="text-muted-foreground text-xs">never</span>;
  return <span className="flex items-center gap-1.5"><StatusPill status={last.status} /><span className="text-muted-foreground text-xs">{last.recipients}✉ · {fmt(last.at)}</span></span>;
};

const Chip = ({ on, children }) => (
  <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${on ? 'text-emerald-600 bg-emerald-500/10' : 'text-muted-foreground bg-secondary'}`}>{children}</span>
);

const NotificationsMonitor = () => {
  const [status, setStatus] = useState(null);
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([superadminApi.getNotificationsStatus(), superadminApi.getNotificationsSends(150)]);
      setStatus(s.data); setSends(h.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load notifications status');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    if (!window.confirm('Evaluate scheduled notifications now? Only digests/birthdays actually due (per each tenant\'s time & timezone) will send. Idempotent — already-sent batches are skipped.')) return;
    setRunning(true);
    try {
      const r = await superadminApi.runNotificationsNow();
      const d = r.data;
      toast.success(`Checked ${d.tenants} tenant(s) — ${d.batches} batch(es), ${d.sent} email(s)${d.errors ? `, ${d.errors} error(s)` : ''}.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run notifications');
    } finally { setRunning(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>;

  const sched = status?.scheduler || {};
  const tenants = status?.tenants || [];

  return (
    <div className="space-y-6">
      <div className="card-hive p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sched.running ? 'bg-emerald-500/15 text-emerald-600' : 'bg-red-500/15 text-red-600'}`}><Activity className="w-5 h-5" /></div>
            <div>
              <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                Notifications scheduler
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${sched.running ? 'text-emerald-600 bg-emerald-500/10' : 'text-red-600 bg-red-500/10'}`}>{sched.running ? 'Running' : 'Not running'}</span>
              </h3>
              <p className="text-sm text-muted-foreground">Checks every {sched.tick_seconds || 60}s and sends digests + birthday reminders in each company's own timezone. Birthdays go out at {sched.birthday_send_time || '08:00'}, 1 week &amp; 2 days ahead.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
            <Button className="btn-primary" onClick={runNow} disabled={running}>{running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />} Run now</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-5">
          <div className="rounded-lg bg-secondary/40 p-3"><div className="text-xs text-muted-foreground">Started</div><div className="text-sm font-medium mt-1">{fmt(sched.started_at)}</div></div>
          <div className="rounded-lg bg-secondary/40 p-3"><div className="text-xs text-muted-foreground">Last check</div><div className="text-sm font-medium mt-1">{fmt(sched.last_check_at)}</div></div>
          <div className="rounded-lg bg-secondary/40 p-3"><div className="text-xs text-muted-foreground">Last run sent</div><div className="text-sm font-medium mt-1">{sched.last_result ? `${sched.last_result.sent} email(s), ${sched.last_result.batches} batch(es)` : '—'}</div></div>
        </div>
      </div>

      <div className="card-hive p-6">
        <h3 className="font-heading font-bold text-lg mb-1">Per-tenant configuration</h3>
        <p className="text-sm text-muted-foreground mb-4">What each company has enabled, and when it last went out.</p>
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 sticky top-0">
                <tr className="text-left">
                  <th className="p-2.5 font-medium">Company</th>
                  <th className="p-2.5 font-medium">Timezone</th>
                  <th className="p-2.5 font-medium"><Sun className="w-3.5 h-3.5 inline mr-1" />Daily</th>
                  <th className="p-2.5 font-medium"><CalendarRange className="w-3.5 h-3.5 inline mr-1" />Weekly</th>
                  <th className="p-2.5 font-medium"><CalendarDays className="w-3.5 h-3.5 inline mr-1" />Monthly</th>
                  <th className="p-2.5 font-medium"><Cake className="w-3.5 h-3.5 inline mr-1" />Birthday</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.tenant_id} className="border-t border-border/50 align-top">
                    <td className="p-2.5 font-medium">{t.company_name}</td>
                    <td className="p-2.5 text-muted-foreground text-xs">{t.timezone}</td>
                    <td className="p-2.5"><div className="space-y-1"><Chip on={t.daily.enabled}>{t.daily.enabled ? t.daily.time : 'off'}</Chip><div><LastCell last={t.daily.last} /></div></div></td>
                    <td className="p-2.5"><div className="space-y-1"><Chip on={t.weekly.enabled}>{t.weekly.enabled ? `${(t.weekly.day||'').slice(0,3)} ${t.weekly.time}` : 'off'}</Chip><div><LastCell last={t.weekly.last} /></div></div></td>
                    <td className="p-2.5"><div className="space-y-1"><Chip on={t.monthly.enabled}>{t.monthly.enabled ? `day ${t.monthly.day} ${t.monthly.time}` : 'off'}</Chip><div><LastCell last={t.monthly.last} /></div></div></td>
                    <td className="p-2.5"><div className="space-y-1"><Chip on={t.birthday.enabled}>{t.birthday.enabled ? 'on' : 'off'}</Chip><div><LastCell last={t.birthday.last} /></div></div></td>
                  </tr>
                ))}
                {tenants.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No tenants.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card-hive p-6">
        <h3 className="font-heading font-bold text-lg mb-1">Send history</h3>
        <p className="text-sm text-muted-foreground mb-4">Every digest and birthday batch, newest first.</p>
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 sticky top-0">
                <tr className="text-left">
                  <th className="p-2.5 font-medium">When</th>
                  <th className="p-2.5 font-medium">Company</th>
                  <th className="p-2.5 font-medium">Kind</th>
                  <th className="p-2.5 font-medium">For</th>
                  <th className="p-2.5 font-medium">Trigger</th>
                  <th className="p-2.5 font-medium">Sent</th>
                  <th className="p-2.5 font-medium">Status</th>
                  <th className="p-2.5 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {sends.map((r) => (
                  <tr key={r.id} className="border-t border-border/50 align-top">
                    <td className="p-2.5 whitespace-nowrap text-muted-foreground">{fmt(r.created_at)}</td>
                    <td className="p-2.5">{r.tenant_name || '—'}</td>
                    <td className="p-2.5"><span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary">{r.kind}</span></td>
                    <td className="p-2.5 text-muted-foreground text-xs whitespace-nowrap">{r.target_date}</td>
                    <td className="p-2.5"><span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary">{r.trigger}</span></td>
                    <td className="p-2.5">{r.recipients}</td>
                    <td className="p-2.5"><StatusPill status={r.status} /></td>
                    <td className="p-2.5 text-muted-foreground text-xs">{r.message}</td>
                  </tr>
                ))}
                {sends.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">Nothing sent yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsMonitor;
